// `GET /api/leagues/:id/calendar?from=&to=` — everything in a league that
// happens at a time, as one reader meets it (scenario D12,
// PLAN-2026-10-30-the-calendar §5).
//
// INSTANTS, NOT DAYS. Which day an entry is on depends on the reader's zone,
// so the server compares instants only and a client lays them out in its own
// days (`timeZone` is the reader's, for labels, as every view's is: B-181).
//
// EACH TIME IS THE CODE'S THAT ACTS ON IT. The league's times are the clock's
// (`leagueAgenda`, from the helpers its planner plans with), the reader's own
// the reads that serve them; `calendarView` in `@fsp/clock` puts them
// together, for the endpoint and the practice league's simulator alike.

import { z } from 'zod';
import { DraftMode } from '../rules/schemas';
import { LeagueRef, LineupProblem, LineupRowView, PlayerSummary, TeamRef } from './views';

const Iso = z.string();

/** The range a request asks for when it names none: two weeks from `from`. */
export const CALENDAR_DEFAULT_DAYS = 14;
/** The longest range served: two of a client's weeks, with room to spare. */
export const CALENDAR_MAX_DAYS = 21;

/** The query. `from` defaults to now and `to` to `CALENDAR_DEFAULT_DAYS` after `from`. */
export const CalendarQuery = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type CalendarQuery = z.infer<typeof CalendarQuery>;

/** One kickoff instant of a week's pro games, and how many start then. */
export const CalendarKickoff = z.object({ at: Iso, games: z.number().int().min(1) });
export type CalendarKickoff = z.infer<typeof CalendarKickoff>;

/** One of the league's weeks that overlaps the range. */
export const CalendarWeek = z.object({
  period: z.number().int().min(1),
  label: z.string(),
  start: Iso,
  end: Iso,
  kind: z.enum(['regular', 'playoff', 'off']),
  /**
   * Whom I play, and the game. Null with no team of mine, a bye, a week
   * played against the field, or before the week's games are written.
   */
  opponent: TeamRef.nullable(),
  matchupId: z.string().nullable(),
  /**
   * My second opponent and game this week, in a league that plays two
   * opponents a week (scenario Q6); null otherwise. Added 2026-11-01.
   */
  secondOpponent: TeamRef.nullable().default(null),
  secondMatchupId: z.string().nullable().default(null),
  /** Every kickoff of the week's pro games, in order. */
  slate: z.array(CalendarKickoff),
  /**
   * The kickoff with the most games, the earliest of them on a tie: the
   * week's main slate, which an early game is before. Null with no games.
   */
  mainSlateAt: Iso.nullable(),
  /** The first instant anything in the week locks, per the league's rule. */
  firstLockAt: Iso.nullable(),
  /** My starters and bench whose club has no game this week. Empty with no team. */
  byes: z.array(PlayerSummary),
  /**
   * My lineup's problems that cost points — an empty slot, or a starter on a
   * bye or ruled out, what scores nothing at lock — exactly as the lineup
   * read says them (`LineupView.health`). Facts, never a player to put in
   * (DIRECTION U3).
   */
  problems: z.array(LineupProblem),
});
export type CalendarWeek = z.infer<typeof CalendarWeek>;

/** One of my players in a kickoff. */
export const CalendarPlayer = z.object({
  player: PlayerSummary,
  /** Reserve and taxi players cannot score, and are not on the calendar. */
  slotKind: z.enum(['starter', 'bench']),
  /** The slot's id, as the lineup has it. */
  slot: z.string(),
  /** "at MIN", "vs GB"; null when the game names no opponent. */
  opponent: z.string().nullable(),
  state: z.enum(['pre', 'live', 'final']),
  /** His points once his game has started; null before, and where none are in. */
  points: z.number().nullable(),
  /** The lineup row's own health. */
  health: LineupRowView.shape.health,
});
export type CalendarPlayer = z.infer<typeof CalendarPlayer>;

const at = {
  at: Iso,
  /** The league's week it falls in; null for one outside every week. */
  period: z.number().int().nullable(),
};

export const CalendarEvent = z.discriminatedUnion('kind', [
  /** A kickoff with a starter or a bench player of mine in it. */
  z.object({
    kind: z.literal('games'),
    ...at,
    players: z.array(CalendarPlayer),
    /** How many of the week's pro games start at this instant. */
    games: z.number().int().min(1),
    /** Before the week's main slate (`CalendarWeek.mainSlateAt`). */
    early: z.boolean(),
    /** These players' slots lock now: the league locks each at its kickoff. */
    locks: z.boolean(),
  }),
  /** The whole lineup locks: a league that locks at the first game or a fixed time. */
  z.object({
    kind: z.literal('lineup-lock'),
    ...at,
    /** My starting slots it locks; null with no team. */
    starters: z.number().int().nullable(),
  }),
  /** A waiver run, or in a league that decides claims as players clear, one of my claims decided. */
  z.object({
    kind: z.literal('waivers'),
    ...at,
    /** My claims it decides, by player; null with no team. */
    claims: z.array(PlayerSummary).nullable(),
    continuous: z.boolean(),
  }),
  /**
   * A trade's moment: `answer` an offer waiting on me, at its answer-by;
   * `awaiting` one I sent, at theirs; `review` one in review, at its end;
   * `executes` an accepted one, when it goes through.
   */
  z.object({
    kind: z.literal('trade'),
    ...at,
    tradeId: z.string(),
    step: z.enum(['answer', 'awaiting', 'review', 'executes']),
    /** The other side; for a trade between two others, the team that offered it. */
    team: TeamRef,
    /** The second team of a trade between two others; null for one of mine. */
    partner: TeamRef.nullable(),
    /** "Kareem Hunt for your Mahomes": what each side gives, from mine when it is mine. */
    summary: z.string(),
    mine: z.boolean(),
    /** In review, and I may still vote against it. */
    canVote: z.boolean(),
  }),
  /** The trade deadline (offers still open are voided), the taxi squad closing, keepers locking. */
  z.object({
    kind: z.literal('deadline'),
    ...at,
    what: z.enum(['trades', 'taxi', 'keepers']),
  }),
  z.object({
    kind: z.literal('draft'),
    ...at,
    mode: DraftMode,
    /** When a live draft's room opens; null for a draft with no room. */
    roomOpensAt: Iso.nullable(),
  }),
  /** A week ends: its results are final, and its awards and recap come. */
  z.object({ kind: z.literal('week-ends'), ...at }),
  /** The first playoff week begins. */
  z.object({ kind: z.literal('playoffs'), ...at }),
  z.object({
    kind: z.literal('vote'),
    ...at,
    vote: z.enum(['rules', 'election']),
    /** The rule change's reason, or the election's name. */
    text: z.string(),
    voted: z.boolean(),
  }),
  /** Pick'em locks for the week. */
  z.object({
    kind: z.literal('pickem'),
    ...at,
    picked: z.number().int().min(0),
    of: z.number().int().min(0),
  }),
]);
export type CalendarEvent = z.infer<typeof CalendarEvent>;
export type CalendarEventKind = CalendarEvent['kind'];

/**
 * THE ORDER AT ONE INSTANT: what ends first (a week, a waiver run, a
 * deadline), then what opens or closes, then locks, then games — so a waiver
 * run at the same minute as a kickoff reads before the game it might fill.
 */
export const CALENDAR_EVENT_ORDER: Readonly<Record<CalendarEventKind, number>> = {
  'week-ends': 0,
  waivers: 1,
  deadline: 2,
  playoffs: 3,
  vote: 4,
  trade: 5,
  draft: 6,
  pickem: 7,
  'lineup-lock': 8,
  games: 9,
};

export const CalendarView = z.object({
  league: LeagueRef,
  from: Iso,
  to: Iso,
  /** The reader's zone, for labels (B-181); every rule behind these times is the league's. */
  timeZone: z.string().min(1),
  myTeamId: z.string().nullable(),
  /** Each of the league's weeks that overlaps the range, in order. Empty with no calendar yet. */
  weeks: z.array(CalendarWeek),
  /** In the order they happen, `CALENDAR_EVENT_ORDER` at one instant. */
  events: z.array(CalendarEvent),
  updatedAt: Iso,
});
export type CalendarView = z.infer<typeof CalendarView>;
